import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
} from '@material-ui/core';
import {ExpandMore, RotateLeft} from '@material-ui/icons';
import {Form, Formik, useFormikContext} from 'formik';
import React, {useEffect, useRef, useState} from 'react';
import useSWR, {mutate} from 'swr';
import * as Yup from 'yup';
import {getPreProcessorParameters} from '../../../api/postprocessor/GetPreProcessorParameters';
import {
  resetParameters,
  UpdateParameterRequest,
  updateParameters,
} from '../../../api/postprocessor/UpdateParameters';
import {Parameter} from '../../../types/model/framework/Parameter';
import {
  getParameterValidationRule,
  ParameterField,
} from '../../PrePostProcessing/Fields/ParameterField';
import {useIntl} from 'react-intl';
import {StudioTooltip} from '../../../base-components/StudioTooltip';
import {toast} from '../../../base-components/StudioToast';
import {getPostProcessorParameters} from '../../../api/postprocessor/GetPostProcessorParameters';
import {ExecutionContainer} from '../../../types/model/framework/ExecutionContainer';
import {PostProcessorType} from '../../../types/postprocessor/PostProcessorType';

type RealtimePostProcessingParametersProps = {
  projectId: string;
  applicationId: string;
  onChange: (postProcessors: Array<Parameter>) => void;
  className?: string;
  emptyMessage?: React.ReactNode;
  renderField?: ParametersPartialFormProps['renderField'];
  spaId?: string;
};

export const RealtimePostProcessingParameters = ({
  projectId,
  applicationId,
  onChange,
  emptyMessage,
  renderField,
  className,
  spaId,
}: RealtimePostProcessingParametersProps) => {
  const {data: postProcessorParametersData} = useSWR(
    getDeployParamsKey(projectId, applicationId, spaId),
    () =>
      getPostProcessorParameters({
        projectId,
        applicationId,
        spaId,
      }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );
  const params = postProcessorParametersData?.body || [];

  const handleChange: ParametersPartialFormProps['onChange'] = values => {
    const updatedParameters =
      params
        ?.filter(param => param.parameterType === 'REALTIME')
        .map(parameter => ({
          ...parameter,
          value: values[parameter.id]?.toString(),
        })) || [];
    onChange(updatedParameters);
  };

  if (params?.length) {
    const realtimeParams = params.filter(param => param.parameterType === 'REALTIME');
    const initialValues = getInitialValues(params);
    const validationSchema = buildValidationSchema(params);

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={() => {}}
      >
        <Form className={className}>
          <ParametersPartialForm
            params={realtimeParams}
            onChange={handleChange}
            renderField={renderField}
          />
        </Form>
      </Formik>
    );
  } else {
    return <div>{emptyMessage}</div> || null;
  }
};

type RealtimePreProcessingParametersProps = {
  projectId: string;
  applicationId: string;
  onChange: (preProcessors: Array<Parameter>) => void;
  className?: string;
  emptyMessage?: React.ReactNode;
  renderField?: ParametersPartialFormProps['renderField'];
};

export const RealtimePreProcessingParameters = ({
  projectId,
  applicationId,
  onChange,
  emptyMessage,
  renderField,
  className,
}: RealtimePreProcessingParametersProps) => {
  const {data} = useSWR(
    projectId && applicationId
      ? [projectId, applicationId, '/preprocessor/parameters/get']
      : null,
    () => getPreProcessorParameters({projectId, applicationId})
  );

  const params =
    data?.body
      ?.filter(param => param.parameterType === 'REALTIME')
      .map(param => ({
        ...param,
      })) || [];

  const handleChange: ParametersPartialFormProps['onChange'] = values => {
    const updatedParameters =
      params.map(parameter => ({
        ...parameter,
        value: values[parameter.id]?.toString(),
      })) || [];
    onChange(updatedParameters);
  };

  if (params?.length) {
    const initialValues = getInitialValues(params);
    const validationSchema = buildValidationSchema(params);

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={() => {}}
      >
        <Form className={className}>
          <ParametersPartialForm
            params={params}
            onChange={handleChange}
            renderField={renderField}
          />
        </Form>
      </Formik>
    );
  } else {
    return <div>{emptyMessage}</div> || null;
  }
};

type DeploymentPostProcessingParametersProps = {
  projectId: string;
  applicationId: string;
  spaId?: string;
  onChange: (parameters: Array<UpdateParameterRequest>) => void;
  titleMessageId: string;
  wrapperClassName: string;
  detailsClassName: string;
  persistChanges?: boolean;
  onValidityChange?: ParametersPartialFormProps['onValidityChange'];
  disabledForm?: boolean;
  renderField?: ParametersPartialFormProps['renderField'];
  className?: string;
  executionContainer?: ExecutionContainer;
  postProcessorType?: PostProcessorType;
};

export const getDeployParamsKey = (
  projectId: string,
  applicationId: string,
  spaId?: string
) =>
  projectId && applicationId
    ? [projectId, applicationId, spaId, '/postprocessor/parameters/get']
    : null;

export const DeploymentPostProcessingParameters = ({
  projectId,
  applicationId,
  spaId,
  onValidityChange,
  onChange,
  titleMessageId,
  wrapperClassName,
  detailsClassName,
  persistChanges,
  renderField,
  disabledForm,
  className,
  executionContainer,
  postProcessorType,
}: DeploymentPostProcessingParametersProps) => {
  const intl = useIntl();

  const {data: postProcessorParametersData} = useSWR(
    getDeployParamsKey(projectId, applicationId, spaId),
    () =>
      getPostProcessorParameters({
        projectId,
        applicationId,
        spaId,
        executionContainer,
        postProcessorType,
      }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );
  const postProcessorParameters = postProcessorParametersData?.body || [];

  const type = 'DEPLOYMENT';
  const params = postProcessorParameters
    ?.map(parameter => ({
      ...parameter,
      readOnly: disabledForm,
    }))
    .filter(param => param.parameterType === type);

  const prepareUpdateParameterRequests = (
    values: Record<string, string | number | undefined>
  ): UpdateParameterRequest[] => {
    return params
      .filter(param => param.parameterType === type)
      .filter(param => values[param.id])
      .map<UpdateParameterRequest>(parameter => ({
        id: parameter.id,
        name: parameter.name,
        value: (values[parameter.id] ?? '').toString(),
      }));
  };

  if (params?.length) {
    const initialValues = getInitialValues(params);
    const validationSchema = buildValidationSchema(params);
    return (
      <TestParametersAccordion
        title={intl.formatMessage({id: titleMessageId})}
        className={wrapperClassName}
        detailsClassName={detailsClassName}
        defaultExpand
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={() => {}}
        >
          <div className={className}>
            <ParametersPartialForm
              params={params}
              onChange={p => onChange(prepareUpdateParameterRequests(p))}
              renderField={renderField}
              onValidityChange={onValidityChange}
              onFieldUpdateFinished={p => {
                if (!persistChanges) {
                  return;
                }
                const parameters = prepareUpdateParameterRequests(p);
                updateParameters({
                  projectId,
                  applicationId,
                  spaId,
                  parameters,
                })
                  .then(() => mutate(getDeployParamsKey(projectId, applicationId, spaId)))
                  .catch(e => toast.error(e));
              }}
            />
          </div>
        </Formik>
        {persistChanges && (
          <StudioTooltip title={intl.formatMessage({id: 'deployments.resetParameters'})}>
            <span>
              <IconButton
                className={wrapperClassName + '_reset'}
                onClick={() =>
                  resetParameters({
                    projectId,
                    applicationId,
                    spaId,
                    parameterType: 'POST_PROCESSOR',
                    executionContainer,
                    postProcessorType,
                  })
                    .then(() =>
                      mutate(getDeployParamsKey(projectId, applicationId, spaId))
                    )
                    .catch(e => toast.error(e))
                }
                disabled={!params.some(x => x.originalValue)}
              >
                <RotateLeft />
              </IconButton>
            </span>
          </StudioTooltip>
        )}
      </TestParametersAccordion>
    );
  } else {
    return <></>;
  }
};

type PreProcessingParametersProps = {
  projectId: string;
  applicationId: string;
  onChange: (preProcessingParameters: Array<UpdateParameterRequest>) => void;
  persistChanges?: boolean;
  onValidityChange?: ParametersPartialFormProps['onValidityChange'];
  titleMessageId: string;
  wrapperClassName: string;
  detailsClassName: string;
  className?: string;
  renderField?: ParametersPartialFormProps['renderField'];
  disabledForm?: boolean;
};

export const PreProcessingParameters = ({
  projectId,
  applicationId,
  onValidityChange,
  onChange,
  titleMessageId,
  persistChanges,
  wrapperClassName,
  detailsClassName,
  className,
  renderField,
  disabledForm,
}: PreProcessingParametersProps) => {
  const intl = useIntl();
  const {data} = useSWR(
    projectId && applicationId
      ? [projectId, applicationId, '/preprocessor/parameters/get']
      : null,
    () => getPreProcessorParameters({projectId, applicationId}),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const params = data?.body
    ?.filter(param => param.parameterType === 'DEPLOYMENT')
    .map(param => ({
      ...param,
      readOnly: disabledForm,
    }));

  const prepareUpdateParameterRequests = (
    values: Record<string, string | number | undefined>
  ): UpdateParameterRequest[] => {
    return (
      params?.map<UpdateParameterRequest>(param => ({
        parameterType: 'PRE_PROCESSOR',
        id: param.id,
        name: param.name,
        value: (values[param.id] ?? '').toString(),
      })) || []
    );
  };

  if (params?.length) {
    const initialValues = getInitialValues(params);
    const validationSchema = buildValidationSchema(params);
    return (
      <TestParametersAccordion
        title={intl.formatMessage({id: titleMessageId})}
        className={wrapperClassName}
        detailsClassName={detailsClassName}
        defaultExpand
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={() => {}}
        >
          <div className={className}>
            <ParametersPartialForm
              params={params}
              onChange={p => onChange(prepareUpdateParameterRequests(p))}
              renderField={renderField}
              onValidityChange={onValidityChange}
              onFieldUpdateFinished={p => {
                if (!persistChanges) {
                  return;
                }
                const parameters = prepareUpdateParameterRequests(p);
                updateParameters({
                  projectId,
                  applicationId,
                  parameters,
                })
                  .then(() =>
                    mutate([projectId, applicationId, '/preprocessor/parameters/get'])
                  )
                  .catch(e => toast.error(e));
              }}
            />
          </div>
        </Formik>
        {persistChanges && (
          <StudioTooltip title={intl.formatMessage({id: 'deployments.resetParameters'})}>
            <span>
              <IconButton
                className={wrapperClassName + '_reset'}
                onClick={() =>
                  resetParameters({
                    projectId,
                    applicationId,
                    parameterType: 'PRE_PROCESSOR',
                  })
                    .then(() =>
                      mutate([projectId, applicationId, '/preprocessor/parameters/get'])
                    )
                    .catch(e => toast.error(e))
                }
                disabled={!params.some(x => x.originalValue)}
              >
                <RotateLeft />
              </IconButton>
            </span>
          </StudioTooltip>
        )}
      </TestParametersAccordion>
    );
  } else {
    return <></>;
  }
};

type ParametersPartialFormProps = {
  params: Array<Parameter & {readOnly?: boolean}>;
  onChange: (values: Record<string, string | number | undefined>) => void;
  onValidityChange?: (isValid: boolean) => void;
  renderField?: (field: React.ReactNode, parameter: Parameter) => React.ReactNode;
  onFieldUpdateFinished?: (updated: Record<string, string | number | undefined>) => void;
};

const ParametersPartialForm = ({
  params,
  onChange,
  onValidityChange,
  renderField = (field: React.ReactNode) => field,
  onFieldUpdateFinished,
}: ParametersPartialFormProps) => {
  const {values, isValid, validateForm} = useFormikContext<
    Record<string, string | number | undefined>
  >();
  const onChangeRef = useRef(onChange);
  const onValidityChangeRef = useRef(onValidityChange);
  const [prev, setPrev] = useState(values);

  useEffect(() => {
    onValidityChangeRef.current?.(isValid);
  }, [isValid]);

  useEffect(() => {
    validateForm().then(errors => {
      const numErrors = Object.entries(errors).length;
      if (numErrors === 0) {
        onChangeRef.current?.(values);
      }
    });
  }, [values, validateForm]);

  const focusOut = () => {
    if (!isValid) {
      return;
    }
    const updated = {} as Record<string, string | number | undefined>;
    for (const key in values) {
      if (prev[key] !== values[key]) {
        updated[key] = values[key];
      }
    }
    if (Object.keys(updated).length === 0) {
      return;
    }
    onFieldUpdateFinished?.(updated);
    setPrev(values);
  };

  return (
    <div onBlur={focusOut}>
      {params?.map(param => {
        const {readOnly, ...parameter} = param;
        return renderField(
          <ParameterField
            key={param.id}
            parameter={parameter}
            fieldName={param.id}
            disabled={Boolean(readOnly)}
          />,
          param
        );
      })}
    </div>
  );
};

const buildValidationSchema = (params: Array<Parameter>) => {
  return Yup.object(
    params.reduce<Record<string, Yup.Schema<unknown>>>((acc, curr) => {
      const rule = getParameterValidationRule(curr);
      if (rule) {
        acc[curr.id] = rule.label(curr.displayName || curr.name).required();
      }
      return acc;
    }, {})
  );
};

const getInitialValues = (params: Array<Parameter>) =>
  params.reduce<Record<string, string | number | undefined>>((acc, curr) => {
    if (curr.dataType === 'FLOATING_POINT_NUMBER' || curr.dataType === 'INTEGER') {
      acc[curr.id] = Number(curr.value);
    } else {
      acc[curr.id] = curr.value;
    }
    return acc;
  }, {});

type TestParametersAccordionProps = {
  className?: string;
  detailsClassName?: string;
  title: string;
  children: React.ReactNode;
  expanded?: boolean | undefined;
  defaultExpand?: boolean | undefined;
};

export const TestParametersAccordion = ({
  className,
  detailsClassName,
  title,
  children,
  expanded,
  defaultExpand,
}: TestParametersAccordionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpand || false);

  return (
    <Accordion
      className={className}
      expanded={expanded === undefined ? isExpanded : expanded}
      elevation={0}
      onChange={() => setIsExpanded(curr => !curr)}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>{title}</AccordionSummary>
      <AccordionDetails className={detailsClassName}>{children}</AccordionDetails>
    </Accordion>
  );
};
